'use client';

import { sendMessageToExtension } from 'scribe-messaging';
import { EXTENSION_IDS } from 'scribe-web-shared/consts/index';
import { CreateFirstActionCondition, SidePanelRoute } from 'scribe-web-shared/enums';
import { EntryPoints } from 'scribe-web-shared/enums/entry-point-enums';
import { isEdge } from 'scribe-web-shared/functions';
import { OptionsState } from 'scribe-web-shared/types/extensionOptionsStateTypes';

import { ExtensionStatus } from '@/_hooks/useExtensionState/types';
import { captureSentryError } from '@/_lib/sentry';
import { Message, Response } from '@/_types/Extension';
import { getRuntimeEnv } from '@/_utils/environment';

declare global {
  interface Window {
    _CursiveRecorder?: any;
  }
}

export const recordingDetails = () => {
  const recorder = global.window && global.window._CursiveRecorder;

  return recorder?.getFullRecorderDetails?.() || recorder?.getRecorderDetails();
};

// TODO: We should build a new abstraction on top of this with strict types for every message type
export const sendExtensionMessage = <T = Response>(
  message: Message,
  callback?: (response: T | { status?: string; unsupportedMessage?: boolean }) => void
): Promise<T | { status?: string; unsupportedMessage?: boolean } | undefined> => {
  return new Promise((resolve) => {
    const { edgeExtensionId, extensionId, nodeEnv, isCICD } = getRuntimeEnv();
    const expectedEnvValues = ['development', 'hosteddevelop', 'staging', 'production'];
    let environment = nodeEnv;

    if (!nodeEnv || !expectedEnvValues.includes(nodeEnv) || isCICD) {
      environment = 'production';
    }

    let suggestedExtensionId = EXTENSION_IDS[isEdge ? 'edge' : 'chrome'][isCICD ? 'production' : environment];

    try {
      const envExtensionId = isEdge ? edgeExtensionId : extensionId;
      const extensionIdOverride = localStorage.getItem('testExtensionId');

      if (extensionIdOverride) {
        suggestedExtensionId = extensionIdOverride;
      } else if (envExtensionId && !isCICD) {
        suggestedExtensionId = envExtensionId;
      }
    } catch (error) {
      captureSentryError(error as string | Error);
    }

    if (!suggestedExtensionId || !global?.chrome?.runtime?.sendMessage) {
      const result = undefined;

      if (callback) callback(result as T);
      resolve(result);
      return;
    }

    chrome.runtime.sendMessage(suggestedExtensionId, message, (response) => {
      if (chrome?.runtime?.lastError) {
        if (
          chrome.runtime.lastError?.message === 'Could not establish connection. Receiving end does not exist.' &&
          nodeEnv !== 'production'
        ) {
          console.log(`Extension with ID of ${suggestedExtensionId} not found, try clearing out testExtensionId in localStorage`);
        }
        const result = undefined;

        if (callback) callback(result as T);
        resolve(result);
        return;
      }
      if (callback) callback(response as T);
      resolve(response);
    });
  });
};

export const handleExtensionMessageWithResponse = async (
  message: Message,
  fallback: any = undefined,
  unsupportedMessageFallback: any = undefined
) => {
  if (!global?.chrome?.runtime?.sendMessage) {
    return fallback;
  }

  const response = await sendExtensionMessage(message);

  if (response && typeof response === 'object' && 'unsupportedMessage' in response && response.unsupportedMessage) {
    return unsupportedMessageFallback;
  }

  return response === undefined ? fallback : response;
};

export const getExtensionUrl = () => {
  return isEdge
    ? 'https://microsoftedge.microsoft.com/addons/detail/scribe/ejommccbnocgekjphflienmbmkallcef'
    : 'https://chrome.google.com/webstore/detail/scribe-by-cursive/okfkdaglfjjjfefdcppliegebpoegaii';
};

/**
 * In order to successfully open the side panel, this should be fired synchronously in the handler for an `onPointerDown` event.
 * @example
 * ```tsx
    import { SidePanelRoute } from 'scribe-web-shared/enums';
    import { openSidePanel } from '@root/app/_utils/extension';

    function MyButton() {
      return (
        <button onPointerDown={() => { openSidePanel(SidePanelRoute.Home) }}>
            Open Side Panel
        </button>
      );
    }

    export default MyButton;
 * ```
 */
export const openSidePanel = (route: SidePanelRoute) => {
  sendExtensionMessage({
    messageType: 'openSidePanel',
    route
  });
};

export const startGuideMe = (startingUrl: string, location: string, scribeData: { id: string; name: string; actionIndex: string }) => {
  sendExtensionMessage({
    messageType: 'openScribeViewer',
    startingUrl,
    location,
    url: startingUrl,
    scribe: {
      id: scribeData.id,
      name: scribeData.name,
      actionIndex: scribeData.actionIndex
    }
  });
};

export const startRecordingOnTab = ({
  tab,
  entryPoint = null,
  selectedTaskId = ''
}: {
  tab: chrome.tabs.Tab;
  entryPoint?: EntryPoints | null;
  selectedTaskId?: string;
}) => {
  sendExtensionMessage({
    messageType: 'browserStartRecording',
    targetTabId: tab.id,
    targetTaskId: selectedTaskId,
    entryPoint
  });
};

export const startRecordingOnNewTab = ({
  selectedTaskId = '',
  entryPoint
}: {
  selectedTaskId?: string;
  entryPoint?: EntryPoints | null;
}) => {
  sendExtensionMessage({
    messageType: 'browserStartRecording',
    startRecordingFromNewTab: true,
    targetTaskId: selectedTaskId,
    entryPoint
  });
};

export const startRecordingFromUrl = (url: string, entryPoint: EntryPoints | null = null) => {
  sendExtensionMessage({
    messageType: 'browserStartRecording',
    startRecordingFromUrl: true,
    url,
    entryPoint
  });
};

export const isVersionGreaterOrEqual = (version1: string | null, version2: string | null) => {
  if (!version1 || !version2) return false;

  const splitV1 = version1.split('.').map((num) => parseInt(num, 10));
  const splitV2 = version2.split('.').map((num) => parseInt(num, 10));

  // Compare major version
  if (splitV1[0] !== undefined && splitV2[0] !== undefined) {
    if (splitV1[0] > splitV2[0]) return true;
    if (splitV1[0] < splitV2[0]) return false;
  }

  // Major versions are equal, compare minor version
  if (splitV1[1] !== undefined && splitV2[1] !== undefined) {
    if (splitV1[1] > splitV2[1]) return true;
    if (splitV1[1] < splitV2[1]) return false;
  }

  // Minor versions are equal, compare patch version
  if (splitV1[2] !== undefined && splitV2[2] !== undefined) {
    if (splitV1[2] > splitV2[2]) return true;
    if (splitV1[2] < splitV2[2]) return false;
  }

  // All segments are equal
  return true;
};

// If you want a hook to handles these messages, you can use the `useExtensionStatus` hook.
export const getExtensionVersion = async () => handleExtensionMessageWithResponse('version');
export const isExtensionInstalled = async () => !!(await handleExtensionMessageWithResponse('version', false));
export const checkExtensionPermissions = async () => handleExtensionMessageWithResponse('checkPermissions', undefined, true);
const getSuggestedExtensionId = () => {
  const { edgeExtensionId, extensionId, nodeEnv, isCICD } = getRuntimeEnv();
  const expectedEnvValues = ['development', 'hosteddevelop', 'staging', 'production'];
  let environment = nodeEnv;

  if (!nodeEnv || !expectedEnvValues.includes(nodeEnv) || isCICD) {
    environment = 'production';
  }

  let suggestedExtensionId = EXTENSION_IDS[isEdge ? 'edge' : 'chrome'][isCICD ? 'production' : environment];

  try {
    const envExtensionId = isEdge ? edgeExtensionId : extensionId;
    const extensionIdOverride = localStorage.getItem('testExtensionId');

    if (extensionIdOverride) {
      suggestedExtensionId = extensionIdOverride;
    } else if (envExtensionId && !isCICD) {
      suggestedExtensionId = envExtensionId;
    }
  } catch (error) {
    captureSentryError(error as string | Error);
  }

  return suggestedExtensionId;
};

export const isExtensionPinned = async () => sendMessageToExtension('isExtensionPinned', undefined, getSuggestedExtensionId());
export const deleteAction = async (actionId: string) =>
  sendMessageToExtension('deleteAction', { tempId: actionId }, getSuggestedExtensionId());
export const isExtensionRecording = async () => handleExtensionMessageWithResponse('isRecording');
export const getRecordingState = async () => handleExtensionMessageWithResponse('getRecordingState');

/**
 * Returns the current status of the extension and the version of the extension.
 * Use with `useExtensionStatus` hook for reactivity and polling.
 * @returns {Promise<{status: ExtensionStatus, version: string, installed: boolean, options: OptionsState}>}
 */

/**
 * Returns the current status of the extension and the version of the extension.
 * Use with `useExtensionStatus` hook for reactivity and polling.
 * @returns {Promise<{status: ExtensionStatus, version: string, installed: boolean, options: OptionsState}>}
 */
export const getExtensionState = async (): Promise<{
  status: ExtensionStatus;
  version: string;
  installed: boolean;
  options: OptionsState;
}> => handleExtensionMessageWithResponse('getExtensionState');
export const getExtensionOptions = async () => handleExtensionMessageWithResponse('options');
export const getBrowserTabs = async () => handleExtensionMessageWithResponse('getTabs', []);
export const updateAction = async (action: any) =>
  sendExtensionMessage({
    messageType: 'updateAction',
    action
  });
/**
 *
 * @returns {Promise<chrome.windows.Window>}
 */
export const getLastFocusedWindow = async () => handleExtensionMessageWithResponse({ messageType: 'getLastFocusedWindow' });
/**
 * @param {Object} options
 * @param {CreateFirstActionCondition=} options.setFirstAction
 * @returns {Promise<{actions: ExtensionAction[]}>}
 */
export const initializeRecordingPreview = async ({ setFirstAction }: { setFirstAction: CreateFirstActionCondition }) =>
  handleExtensionMessageWithResponse({ messageType: 'initializeRecordingPreview', setFirstAction });

export const checkServerAvailability = async () => handleExtensionMessageWithResponse('checkServerAvailability', false);
